import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

export interface KeyStat {
    label: string;
    value: string;
}

/** Column-count ladder keyed by number of stats, driven by CONTAINER width
 *  (`@container/keystats`) so the row wraps by its own width, not the viewport.
 *  Stacked (1-col) below `@xl`; an intermediate wrap (e.g. 5 → 3-across, "2×3")
 *  before going full-width. Static classes so Tailwind keeps them in the build. */
const GRID_COLS: Record<number, string> = {
    2: "@xl/keystats:grid-cols-2",
    3: "@xl/keystats:grid-cols-3",
    4: "@xl/keystats:grid-cols-2 @2xl/keystats:grid-cols-4",
    // Drop to the 3-across ("2×3") wrap by @5xl so the wide $-values aren't
    // squeezed at the awkward in-between widths.
    5: "@xl/keystats:grid-cols-3 @5xl/keystats:grid-cols-5"
};

/**
 * Generic key-stats row — `n` fine-print-label-over-H2-value cells, hairline
 * divided, top+bottom bordered, full-bleed (each cell owns its padding). The
 * same mechanism the Portfolio / Positions / position-detail pages use,
 * hoisted here (structurally identical to the app's `KeyStatsRow`) so the
 * shared `ProgramKeyStats` View needs no app import.
 */
export function KeyStatsRow({
    stats,
    topBorder = true
}: {
    stats: KeyStat[];
    /** Draw the top rule. Default `true` (standalone banded row). Set `false`
     *  when the row sits directly under an already-bordered page header so the
     *  two rules don't stack into a 2px line. */
    topBorder?: boolean;
}): React.ReactElement {
    const cols = GRID_COLS[stats.length] ?? "@xl/keystats:grid-cols-4";
    return (
        <section
            className={`@container/keystats ${
                topBorder ? "border-y" : "border-b"
            } border-line-base`}
        >
            {/* Stacked (<@xl): a single column of compact left/right rows with NO
                per-row divider — only the section's top/bottom rule frames it.
                @xl+: the n-across grid. Hairlines come from a 1px gap over a
                line-colored background (cells sit on the ground surface) rather
                than `divide-x`, so when the row WRAPS to multiple lines the
                dividers render correctly in both directions with no stray edges. */}
            <div
                className={`grid grid-cols-1 ${cols} py-2 @xl/keystats:py-0 @xl/keystats:gap-px @xl/keystats:bg-line-base`}
            >
                {stats.map((s) => (
                    <StatCell key={s.label} label={s.label} value={s.value} />
                ))}
                {/* 5 stats in the 3-col wrap (@xl–@4xl) leave one empty trailing
                    slot; without a cell there the grid's line-colored background
                    shows through. Fill it with a ground cell (shown only in that
                    range) so the blank reads as plain surface. */}
                {stats.length === 5 && (
                    <div
                        aria-hidden
                        className="bg-surface-ground hidden @xl/keystats:block @5xl/keystats:hidden"
                    />
                )}
            </div>
        </section>
    );
}

function StatCell({
    label,
    value
}: {
    label: string;
    value: string;
}): React.ReactElement {
    return (
        <div className="px-4 md:px-6 py-1 @xl/keystats:py-5 @xl/keystats:bg-surface-ground">
            {/* Stacked (<@xl) — label ↔ value on one line, same type variant
                (color is the only differentiator), space-efficient. */}
            <div className="flex items-baseline justify-between gap-4 @xl/keystats:hidden">
                <Text
                    variant="body"
                    render={<span />}
                    className="text-foreground-muted truncate"
                >
                    {label}
                </Text>
                <Text
                    variant="body"
                    render={<span />}
                    className="text-foreground-base shrink-0"
                >
                    {value}
                </Text>
            </div>
            {/* @xl+ — fine-print label over an H2 value (the dashboard cell). */}
            <div className="hidden @xl/keystats:block">
                <Text
                    variant="fine-print"
                    render={<span />}
                    className="text-foreground-muted block"
                >
                    {label}
                </Text>
                <Text
                    variant="h2"
                    render={<span />}
                    className="text-foreground-base block mt-1"
                >
                    {value}
                </Text>
            </div>
        </div>
    );
}

export interface ProgramKeyStatsProps {
    /** The already-derived key-stat cells (pre-formatted values). */
    stats: KeyStat[];
}

/**
 * ProgramKeyStats — **View** (props-only). Renders the active-program
 * key-stats row: the same full-bleed `KeyStatsRow` mechanism the Portfolio /
 * Positions / position-detail pages use, scoped to a single program. Sits at
 * the top of the program read view, under the title block.
 *
 * Pure render: it takes the already-computed `KeyStat[]` and hands it to the
 * `KeyStatsRow`. All derivation lives in `ProgramKeyStatsContainer`
 * (app-side). This is the sim/View seam — the sim renders this View with
 * fixture stats and no mock reads.
 *
 * Hoisted into `shared/widgets/yaas/programs` so external consumers (the
 * "+New Program" wiki doc) can render the bare key-stats row fixture-driven,
 * with no wallet / RPC / orgApiFetch. The app keeps a re-export shim at the old
 * path so `yaas-client` compiles unchanged.
 */
export function ProgramKeyStats({
    stats
}: ProgramKeyStatsProps): React.ReactElement {
    return <KeyStatsRow stats={stats} />;
}
